/**
 * Recalculer les prix des gammes d'une marque d'après ses conditions d'achat (lot P2, registre B26).
 *
 * « Toute la marque d'un coup » (décision de Benjamin, 2026-10-15) : une seule revue pour TOUTES les gammes de la marque,
 * un brouillon par gamme dont au moins un prix changerait, puis « Publier les N brouillons » — la publication calcule et
 * fige les prix. **Rien n'est écrit dans une gamme existante** : les prix d'une version publiée ne bougent jamais, un
 * nouveau calcul est une nouvelle version, que l'on ouvre et que l'on publie.
 *
 * La revue dit aussi ce qui MANQUE pour que des lignes aient un prix (une famille sans condition, une référence hors
 * tarif…) : c'est la liste de ce qu'il reste à saisir. Rien n'est jamais deviné.
 *
 * Tout est financier : la carte n'est montrée qu'à qui gère les gammes ET gère et voit les prix (l'API refuse sinon).
 */
import { useEffect, useMemo, useState } from 'react';

import { ApiError, api, type BilanRecalculPrix, type BrouillonEnAttenteTarif, type RevueRecalculPrix } from '../api/client';
import type { DroitsGammes } from '../domaine/droits';
import { aujourdhuiLocal } from '../domaine/gammes-versions';
import { lienVersGamme } from '../domaine/gammes-tarif';
import {
  bilanPublication,
  confirmationPublicationGroupee,
  publiables,
  publierEnSerie,
} from '../domaine/publication-groupee';
import {
  TITRE_REFUS_RECALCUL,
  basculerGamme,
  cocherToutes,
  erreurDeLaDate,
  estAJour,
  estRienAChanger,
  gammesAEnvoyer,
  gammesCocheesParDefaut,
  messageBilan,
  phraseManque,
  resumeEnPhrase,
  titreManque,
  totauxDe,
} from '../domaine/recalcul-prix';
import { dateFrTarif, lignesFr, nombreFr } from '../domaine/tarifs';
import { accordFr } from '../domaine/gammes-tarif';
import { Champ, ErreurFormulaire } from './Formulaire';

export function RecalculPrixGammes({
  partyId,
  partyName,
  droits,
}: {
  partyId: string;
  partyName: string;
  droits: DroitsGammes;
}) {
  const [ouvert, setOuvert] = useState(false);
  const [revue, setRevue] = useState<RevueRecalculPrix | null>(null);
  const [erreurLecture, setErreurLecture] = useState<string | null>(null);
  const [relecture, setRelecture] = useState(0);
  const [cochees, setCochees] = useState<ReadonlySet<string>>(new Set());
  const [validFrom, setValidFrom] = useState('');
  const [enCours, setEnCours] = useState(false);
  const [erreurAction, setErreurAction] = useState<ApiError | Error | null>(null);
  const [bilan, setBilan] = useState<BilanRecalculPrix | null>(null);
  // Les brouillons de recalcul qui attendent leur publication : relus à part, pour rester à jour après la création.
  const [enAttente, setEnAttente] = useState<readonly BrouillonEnAttenteTarif[]>([]);
  const [progression, setProgression] = useState<{ readonly faits: number; readonly total: number } | null>(null);
  const [bilanPub, setBilanPub] = useState<{ readonly succes: string | null; readonly refus: string | null } | null>(null);

  // Une autre marque : on repart de zéro.
  useEffect(() => {
    setOuvert(false);
    setRevue(null);
    setBilan(null);
    setBilanPub(null);
    setErreurAction(null);
  }, [partyId]);

  useEffect(() => {
    if (!ouvert) return;
    let annule = false;
    setRevue(null);
    setErreurLecture(null);
    api
      .revueRecalculPrix(partyId)
      .then((r) => {
        if (annule) return;
        setRevue(r);
        setEnAttente(r.brouillonsEnAttente);
        setCochees(gammesCocheesParDefaut(r.gammes));
        setValidFrom(r.dateEffetProposee);
      })
      .catch((e: unknown) => {
        if (!annule) setErreurLecture(e instanceof ApiError || e instanceof Error ? e.message : 'Lecture impossible.');
      });
    return () => {
      annule = true;
    };
  }, [ouvert, partyId, relecture]);

  // Pendant la publication d'une série, fermer l'onglet laisserait des gammes publiées et d'autres non.
  const aGarder = progression !== null;
  useEffect(() => {
    if (!aGarder) return;
    const avantFermeture = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', avantFermeture);
    return () => window.removeEventListener('beforeunload', avantFermeture);
  }, [aGarder]);

  const envoyees = useMemo(() => (revue ? gammesAEnvoyer(revue.gammes, cochees) : []), [revue, cochees]);
  const totaux = useMemo(() => (revue ? totauxDe(revue.gammes, cochees) : null), [revue, cochees]);
  const erreurDate = revue ? erreurDeLaDate(validFrom, envoyees, revue.gammes) : null;
  const peutCreer = revue !== null && !enCours && bilan === null && envoyees.length > 0 && erreurDate === null;
  const aPublier = useMemo(() => publiables(enAttente), [enAttente]);

  const creer = async () => {
    if (!revue || !peutCreer) return;
    setEnCours(true);
    setErreurAction(null);
    setBilanPub(null);
    try {
      const resultat = await api.appliquerRecalculPrix(partyId, { gammes: envoyees, validFrom });
      setBilan(resultat);
      if (resultat.brouillons > 0) {
        // Les brouillons à publier sont retrouvés par le serveur (date d'effet, version en vigueur) : on les relit.
        const r = await api.revueRecalculPrix(partyId).catch(() => null);
        if (r) setEnAttente(r.brouillonsEnAttente);
      }
    } catch (e) {
      setErreurAction(e instanceof ApiError || e instanceof Error ? e : new Error('Création impossible.'));
    } finally {
      setEnCours(false);
    }
  };

  /** Publie d'un coup les brouillons en attente : UNE confirmation, puis chaque gamme l'une après l'autre. */
  const publier = async () => {
    if (enCours || aPublier.length === 0) return;
    if (!window.confirm(confirmationPublicationGroupee(enAttente, aujourdhuiLocal()))) return;
    setEnCours(true);
    setErreurAction(null);
    setBilanPub(null);
    setProgression({ faits: 0, total: aPublier.length });
    try {
      const resultats = await publierEnSerie(
        aPublier,
        (b) => api.publishMaintenanceVersion(b.profileId, b.versionId),
        (faits, total) => setProgression({ faits, total }),
      );
      setBilanPub(bilanPublication(resultats));
      // Les gammes publiées ont une nouvelle version en vigueur : tout ce que la revue montrait est périmé.
      setBilan(null);
      setRelecture((n) => n + 1);
    } catch (e) {
      setErreurAction(e instanceof ApiError || e instanceof Error ? e : new Error('Publication impossible.'));
    } finally {
      setProgression(null);
      setEnCours(false);
    }
  };

  if (!ouvert) {
    return (
      <div className="carte">
        <div className="carte-corps">
          <h3 style={{ marginTop: 0 }}>Prix de vente des gammes</h3>
          <p className="aide">
            Le prix de vente d’une ligne de gamme se calcule à la <strong>publication</strong> d’une version, avec les conditions
            d’achat du moment, puis il est <strong>figé</strong>. Quand tu saisis ou changes une condition, les gammes déjà publiées ne
            bougent pas : recalcule-les ici, <strong>toute la marque d’un coup</strong>. Rien n’est écrit tant que tu n’as pas
            publié les brouillons.
          </p>
          <div className="saisie-ligne">
            <button type="button" className="secondaire" onClick={() => setOuvert(true)}>
              Recalculer les prix des gammes de {partyName}…
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (erreurLecture) {
    return (
      <div className="carte">
        <div className="carte-corps maj-gammes-tarif">
          <div className="message erreur">{erreurLecture}</div>
          <div className="saisie-ligne">
            <button type="button" className="secondaire" onClick={() => setOuvert(false)}>
              Fermer
            </button>
          </div>
        </div>
      </div>
    );
  }
  if (!revue || !totaux) {
    return (
      <div className="carte">
        <div className="carte-corps">
          <div className="vide">Calcul des prix des gammes de {partyName} en cours…</div>
        </div>
      </div>
    );
  }

  const idsDuBilan = new Set(bilan?.gammes.flatMap((g) => (g.brouillon ? [g.brouillon.id] : [])) ?? []);
  const toutesDansLeBilan = bilan !== null && enAttente.every((b) => idsDuBilan.has(b.versionId));
  const nbAChanger = revue.gammes.filter((g) => !estAJour(g)).length;

  return (
    <div className="carte">
      <div className="carte-corps maj-gammes-tarif">
        <div className="gammes-liste-outils">
          <h3 style={{ margin: 0 }}>Prix de vente des gammes de {revue.marque.nom}</h3>
          <button type="button" className="secondaire discret" disabled={enCours} onClick={() => setOuvert(false)}>
            Fermer
          </button>
        </div>

        <div className="message info">
          Les prix sont calculés <strong>au {dateFrTarif(revue.jour)}</strong> avec les conditions d’achat en cours de {revue.marque.nom}.{' '}
          <strong>Rien n’est écrit dans une gamme existante</strong> : l’application crée un <strong>brouillon par gamme concernée</strong>,
          que tu publies d’un coup (la publication calcule et fige les prix).
        </div>

        {/* Le résultat de la création, EN HAUT : c'est ce qu'on veut lire (avec un lien vers chaque brouillon). */}
        {bilan && <BilanDeLaCreation bilan={bilan} />}

        {bilanPub && (
          <div className="maj-gammes-bilan">
            {bilanPub.succes && <div className="message succes">{bilanPub.succes} Les machines rattachées suivent la gamme.</div>}
            {bilanPub.refus && <div className="message erreur">{bilanPub.refus}</div>}
          </div>
        )}

        {revue.nbConditions === 0 && (
          <div className="message avertissement">
            <strong>{revue.marque.nom} n’a aucune condition d’achat en cours.</strong> Aucun prix de vente ne peut se calculer : saisis
            d’abord au moins une condition (pour toute la marque, par famille du tarif ou par référence), dans le tableau ci-dessous.
          </div>
        )}

        {revue.manques.length > 0 && (
          <div className="message avertissement">
            <strong>Ce qui empêche des prix de se calculer</strong>
            <ul className="details-erreur">
              {revue.manques.map((m) => (
                <li key={`${m.chaine}-${m.raison}-${m.famille ?? ''}`}>
                  <strong>{titreManque(m)}.</strong> {phraseManque(m)}
                </li>
              ))}
            </ul>
          </div>
        )}

        {enAttente.length > 0 && (
          <div className="message avertissement maj-gammes-attente">
            <strong>
              {accordFr(enAttente.length, 'brouillon de recalcul attend', 'brouillons de recalcul attendent')} sa publication
              {toutesDansLeBilan ? '.' : ' :'}
            </strong>
            {!toutesDansLeBilan && (
              <ul className="details-erreur">
                {enAttente.map((b) => (
                  <li key={b.versionId}>
                    <a href={lienVersGamme(b.profileId, b.versionId)} target="_blank" rel="noreferrer">
                      {b.code} — brouillon v{b.versionNumber}
                    </a>
                    {b.perimee && ' — la version en vigueur de la gamme a changé depuis : à relire avant de publier, non publié d’un coup.'}
                  </li>
                ))}
              </ul>
            )}
            {aPublier.length > 0 &&
              (droits.peutPublier ? (
                <div className="saisie-ligne">
                  <button type="button" disabled={enCours} onClick={() => void publier()}>
                    {progression
                      ? `Publication ${progression.faits}/${progression.total}…`
                      : aPublier.length > 1
                        ? `Publier les ${aPublier.length} brouillons`
                        : 'Publier le brouillon'}
                  </button>
                  <span className="aide">Une seule confirmation, puis chaque gamme est publiée l’une après l’autre.</span>
                </div>
              ) : (
                <p className="aide">Seul un compte autorisé à publier les gammes peut les publier : ouvre chaque brouillon pour le relire.</p>
              ))}
          </div>
        )}

        {revue.ignorees.length > 0 && !bilan && (
          <details className="maj-gammes-ignorees">
            <summary>
              {accordFr(revue.ignorees.length, 'gamme non traitée', 'gammes non traitées')} (brouillon déjà ouvert, version à venir…)
            </summary>
            <ul className="details-erreur">
              {revue.ignorees.map((g) => (
                <li key={g.profileId}>
                  <strong>{g.code}</strong> — {g.detail}
                </li>
              ))}
            </ul>
          </details>
        )}

        {/* Une fois les brouillons créés, la sélection est appliquée : on ne montre plus le tableau (« Relire » le rend). */}
        {!bilan && (
          <>
            <p className="aide">
              {accordFr(revue.gammes.length, 'gamme traitable', 'gammes traitables')} ({nbAChanger} avec au moins un prix qui changerait
              ; les autres sont à jour).
            </p>

            <div className="saisie-ligne maj-gammes-cocher">
              <button type="button" className="secondaire discret" disabled={enCours} onClick={() => setCochees(cocherToutes(revue.gammes, true))}>
                Cocher celles à recalculer
              </button>
              <button type="button" className="secondaire discret" disabled={enCours} onClick={() => setCochees(cocherToutes(revue.gammes, false))}>
                Tout décocher
              </button>
            </div>

            {revue.gammes.length === 0 ? (
              <div className="vide">Aucune gamme de cette marque n’est traitable pour l’instant.</div>
            ) : (
              <div className="tableau-defilant">
                <table className="tableau-ecarts maj-gammes-table">
                  <thead>
                    <tr>
                      <th />
                      <th>Gamme</th>
                      <th>Version en vigueur</th>
                      <th>Ce que le recalcul ferait</th>
                    </tr>
                  </thead>
                  <tbody>
                    {revue.gammes.map((g) => (
                      <tr key={g.profileId} className={cochees.has(g.profileId) ? 'maj-gammes-cochee' : undefined}>
                        <td className="maj-gammes-case">
                          <input
                            type="checkbox"
                            aria-label={`Recalculer les prix de ${g.code}`}
                            checked={cochees.has(g.profileId)}
                            disabled={enCours}
                            onChange={(e) => setCochees((c) => basculerGamme(c, g.profileId, e.target.checked))}
                          />
                        </td>
                        <td>
                          <strong>{g.code}</strong>
                          <div className="aide">{g.label}</div>
                        </td>
                        <td>
                          v{g.versionNumber} <span className="aide">· depuis le {dateFrTarif(g.validFrom)}</span>
                          <div className="aide">{lignesFr(g.resume.nbLignes)} à chiffrer</div>
                        </td>
                        <td>
                          {estAJour(g) ? <span className="aide">{resumeEnPhrase(g.resume)}</span> : resumeEnPhrase(g.resume)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        <div className="editeur-barre">
          <ErreurFormulaire
            message={erreurAction?.message ?? null}
            details={erreurAction instanceof ApiError ? erreurAction.details : undefined}
          />
          {!bilan && (
            <Champ
              libelle="Date d’effet des brouillons"
              requis
              aide="Aujourd’hui par défaut : la version publiée remplace la version en vigueur à partir de cette date."
            >
              <input type="date" value={validFrom} disabled={enCours} onChange={(e) => setValidFrom(e.target.value)} />
              {erreurDate && <div className="message erreur">{erreurDate}</div>}
            </Champ>
          )}
          <div className="editeur-barre-actions">
            <span className="aide">
              {bilan
                ? 'Brouillons créés : voir le détail ci-dessus.'
                : envoyees.length === 0
                  ? 'Aucune gamme cochée'
                  : `${accordFr(totaux.gammes, 'gamme', 'gammes')} · ${nombreFr(totaux.nouveaux + totaux.changent)} prix de vente à calculer ou à modifier`}
            </span>
            <div className="saisie-ligne">
              {bilan ? (
                <button
                  type="button"
                  className="secondaire"
                  onClick={() => {
                    setBilan(null);
                    setRelecture((n) => n + 1);
                  }}
                >
                  Relire la revue
                </button>
              ) : null}
              {!bilan && (
                <button type="button" disabled={!peutCreer} onClick={() => void creer()}>
                  {enCours
                    ? 'Création…'
                    : envoyees.length > 0
                      ? `Créer ${accordFr(envoyees.length, 'brouillon', 'brouillons')}`
                      : 'Créer les brouillons'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function BilanDeLaCreation({ bilan }: { bilan: BilanRecalculPrix }) {
  const creees = bilan.gammes.filter((g) => g.brouillon !== null);
  const rienAChanger = bilan.gammes.filter((g) => estRienAChanger(g));
  const refusees = bilan.gammes.filter((g) => g.brouillon === null && !estRienAChanger(g));

  return (
    <div className="maj-gammes-bilan">
      <div className={`message ${creees.length > 0 ? 'succes' : 'avertissement'}`}>
        {messageBilan(bilan)} Date d’effet : <strong>{dateFrTarif(bilan.validFrom)}</strong>.
      </div>
      {creees.length > 0 && (
        <>
          <h3>Brouillons créés ({nombreFr(creees.length)})</h3>
          <ul className="details-erreur">
            {creees.map((g) => (
              <li key={g.profileId}>
                {g.brouillon ? (
                  <a href={lienVersGamme(g.profileId, g.brouillon.id)} target="_blank" rel="noreferrer">
                    {g.code} — brouillon v{g.brouillon.versionNumber}
                  </a>
                ) : (
                  g.code
                )}
                {g.resume && <span className="aide"> ({resumeEnPhrase(g.resume)})</span>}
              </li>
            ))}
          </ul>
        </>
      )}
      {refusees.length > 0 && (
        <>
          <h3>Gammes refusées ({nombreFr(refusees.length)})</h3>
          <ul className="details-erreur">
            {refusees.map((g) => (
              <li key={g.profileId}>
                <strong>{g.code || g.profileId}</strong> — {g.refus ? `${TITRE_REFUS_RECALCUL[g.refus.code]} : ${g.refus.message}` : ''}
              </li>
            ))}
          </ul>
        </>
      )}
      {rienAChanger.length > 0 && (
        <p className="aide">{accordFr(rienAChanger.length, 'gamme', 'gammes')} sans aucun prix qui change.</p>
      )}
    </div>
  );
}
